import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import useQuery from 'hooks/useQuery';
import api from 'services/api';
import Text from 'v3/components/Text';
import Select from 'v3/components/Select';
import Button from 'v3/components/Button';
import Modal from 'v3/components/Modal';
import { useSnackbar } from 'v3/components/Snackbar';

import { FaInfoCircle } from 'react-icons/fa';
import {
  fetchOwners,
  fetchWagonsBrands,
  fetchVehicleBodiesPlates,
  fetchWagonsByIds,
  fetchBodies,
  fetchVehicleBodyTypeByIds,
  fetchPersonById,
} from 'utils/fetches';

export function VehicleBodiesFilter({
  handleClose,
  setQtdAdvancedFilters,
  showAdvancedFilters,
  fetchVehicleBodies,
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const query = useQuery();
  const [loading] = useState(false);

  const [vehicleBodies, setVehicleBodies] = useState([]);
  const [vehicleBodyType, setVehicleBodyType] = useState([]);

  const [brand, setBrand] = useState(null);
  const [modelsOptions, setModelsOptions] = useState([]);
  const [owner, setOwner] = useState(null);
  const [vehicleBodyTypeOptions, setVehicleBodyTypeOptions] = useState([]);
  const [model, setModel] = useState(null);

  async function handleChangeBrand(value) {
    try {
      if (value?.id) {
        const response = await api.get(`v3/models/${value.id}`);
        setModelsOptions(response.data);
        query.set('brandId', value?.id);
        query.set('brandName', value?.name);
      }
    } catch (error) {
      if (value?.id) snackbar.show('Erro ao buscar modelos', { type: 'error' });
    }
    if (value === null) {
      setModelsOptions([]);
      query.delete('brandId');
      query.delete('brandName');
      query.delete('model');
      setModel(null);
    }

    setBrand(value);
  }

  useEffect(() => {
    fetchBodies().then(setVehicleBodyTypeOptions);
    getQueryParams();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      navigate({ search: query.toString() });
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [vehicleBodies, brand, model, owner, vehicleBodyType]);

  useEffect(() => {
    const isNotEmpty = value =>
      value !== null && value !== undefined && value !== '';

    const advancedFilters = {
      model,
      owner,
      brand
    };

    setQtdAdvancedFilters(
      Object.values(advancedFilters).filter(isNotEmpty).length
    );
  }, [model, owner, brand]);

  function onSubmit(event) {
    event.preventDefault();
    fetchVehicleBodies();
  }

  async function getQueryParams() {

    if (query.has('vehicleBodies[]')) {
      const vehicleBodiesIds = query.getAll('vehicleBodies[]').map(id => Number(id));
      const allVehicleBodies = await fetchWagonsByIds(vehicleBodiesIds)
      setVehicleBodies(allVehicleBodies);
    }

    if (query.has('vehicleBodyType[]')) {
      const vehicleBodyTypeIds = query.getAll('vehicleBodyType[]').map(id => Number(id));
      const allVehicleBodyTypes = await fetchVehicleBodyTypeByIds(vehicleBodyTypeIds)
      setVehicleBodyType(allVehicleBodyTypes);
    }

    if (query.has('owner')) {
      const ownerId = Number(query.get('owner'));
      const selectedOwner = await fetchPersonById(ownerId);
      setOwner(selectedOwner);
    }

    if (query.has('brandId')) {
      const id = Number(query.get('brandId'));
      const modelOptionsForSelectedBrand = await api.get(`v3/models/${id}`);
      setBrand({ id: id, name: query.get('brandName') });
      setModelsOptions(modelOptionsForSelectedBrand.data);
    }

    if (query.has('model')) {
      setModel({ label: query.get('model'), id: Number(query.get('brandId')) });
    }
  }

  return (
    <>
      <Modal
        show={showAdvancedFilters}
        heading="Filtro Avançado"
        onHide={handleClose}
        body={
          <Row className="p-1 form" style={{ overflow: 'visible' }}>
            <Col md={6} xs={12} className="mb-3">
              <Select.Async
                label="Proprietário do documento"
                onSearch={value => fetchOwners(value)}
                value={owner}
                onChange={value => {
                  query.delete('owner');
                  if (value) {
                    query.set('owner', value?.id);
                  }
                  setOwner(value);
                }}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.id}
              />
            </Col>
            <Col md={6} xs={12}>
              <Select.Async
                label={
                  <div className="d-flex align-items-center">
                    <FaInfoCircle
                      title="Selecione uma marca para filtrar por modelo"
                      className="mr-2"
                      size={14}
                    />
                    Marca
                  </div>
                }
                onSearch={fetchWagonsBrands}
                value={brand}
                onChange={value => {
                  handleChangeBrand(value);
                }}
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
              />
            </Col>
            <Col md={6} xs={12}>
              <Select
                label="Modelo"
                value={model}
                options={modelsOptions}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.label}
                noOptionsMessageText="Selecione uma marca primeiro"
                onChange={value => {
                  if (value) {
                    query.set('model', value?.label);
                    setModel(value);
                  } else {
                    query.delete('model');
                    setModel(value);
                  }
                }}
              />
            </Col>
          </Row>
        }
        footer={
          <Row style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              loading={loading}
              onClick={() => {
                fetchVehicleBodies();
                handleClose();
              }}
            >
              <Text color="white" weight={500}>
                Filtrar
              </Text>
            </Button>
          </Row>
        }
      />

      <Row style={{ display: 'flex', alignItems: 'center' }}>
        <Col md={4} xs={12} className="mb-3">
          <Select.Async
            labelColor="#ffffff"
            modalHeading="Placa da carroceria"
            modalBodyTitle="Placas:"
            label="Placa da carroceria"
            onSearch={value => fetchVehicleBodiesPlates(value)}
            value={vehicleBodies}
            onChange={value => {
              if (Array.isArray(value)) {
                query.delete('vehicleBodies[]');
                value
                  .map(plate => plate.id)
                  .forEach(processed => {
                    query.append('vehicleBodies[]', processed);
                  })
              } else {
                query.delete('vehicleBodies[]')
              }
              setVehicleBodies(value);
            }}
            horizontal
            multiple
            getOptionLabel={option => option.plate}
            getOptionValue={option => option.id}
          />
        </Col>
        <Col md={4} xs={12} className="mb-3">
          <Select.Async
            labelColor="#ffffff"
            modalHeading="Tipo de carroceria"
            modalBodyTitle="Tipos:"
            label="Tipo de carroceria"
            onSearch={value => fetchBodies(value)}
            value={vehicleBodyType}
            onChange={value => {
              if (Array.isArray(value)) {
                query.delete('vehicleBodyType[]');
                value
                  .map(body => body.id)
                  .forEach(processed => {
                    query.append('vehicleBodyType[]', processed);
                  })
              } else {
                query.delete('vehicleBodyType[]')
              }
              setVehicleBodyType(value);
            }}
            horizontal
            multiple
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id}
            options={vehicleBodyTypeOptions}
          />
        </Col>
        <Col md={2} xs={12} className="vertical bottom">
          <Button
            variant="success"
            loading={loading}
            style={{ marginTop: 12 }}
            onClick={onSubmit}
          >
            <Text
              type="regular"
              weight={500}
            >
              Filtrar
            </Text>
          </Button>
        </Col>
      </Row>
    </>
  );
}
